'use client';

import React from 'react';
import dynamic from "next/dynamic";
import Link from "next/link";
import Image from "next/image";
import { Link as MuiLink, Stack, Typography, Box, Container, Grid, TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import theme, { blueGradientSxDown } from '@/theme/theme';
import Footer from '@/components/layout/footer';
import heroBg from "@/assets/images/heroBG.webp";
import mobileHeroBg from "@/assets/images/mobileHeroBg.webp";

const HeroGridAnimation = dynamic(() => import("@/components/home/heroGridAnimation/heroGridAnimation"), { ssr: false });

export interface CookieRow {
    name: string;
    supplier: string;
    scope: string;
    term: string;
    category: string;
    link: string;
    linkText: string;
}

export interface LinkItem {
    text: string;
    href: string;
}

export type ContentItem = string | { table: CookieRow[] } | { list: LinkItem[] };

export interface LegalSection {
    id: string;
    title: string;
    content: ContentItem[];
}

interface LegalPageLayoutProps {
    title: string;
    description: React.ReactNode;
    lastUpdated: string;
    data: LegalSection[];
}

function LegalHero({ title, description, lastUpdated }: Omit<LegalPageLayoutProps, 'data'>) {
    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            width="100%"
            minHeight={{ xs: "35vh", lg: "50vh" }}
            paddingX={4}
            position="relative"
            sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}` }}
        >
            <Stack width="100%" height="100%" sx={{
                position: "absolute", top: 0, left: 0, zIndex: 0, opacity: 0.55, display: { xs: "block", lg: "none" }
            }}>
                <Image src={mobileHeroBg} alt="mobile hero bg" fill className="mobile-only" priority style={{ objectFit: "cover" }} />
            </Stack>
            <Stack height={"120px"} width="100%" sx={{
                background: "linear-gradient(-180deg, #0B0B0B 0%, rgba(11, 11, 11, 0) 100%)",
                position: "absolute", top: 0, zIndex: 1, display: { xs: "none", lg: "block" }
            }} />
            <Image
                src={heroBg}
                fill
                alt="background"
                style={{ position: "absolute", inset: 0, zIndex: 1, objectFit: "cover" }}
                priority
                className="desktop-only"
            />
            <Stack height="100%" width="100%" sx={{
                position: "absolute", inset: 0, zIndex: 1, opacity: 0.75, display: { xs: "none", lg: "block" }
            }}>
                <HeroGridAnimation />
            </Stack>
            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, pt: { xs: 8, md: 16 }, pb: { xs: 4, md: 8 } }}>
                <Stack gap={2}>
                    <Box sx={{ display: 'inline-flex', px: 2, py: 0.5, borderRadius: 2, border: `1px solid ${theme.palette.brand.mgsPurple1.main}`, bgcolor: 'rgba(56, 75, 255, 0.1)', width: 'fit-content', mb: 2 }}>
                        <Typography variant="caption" sx={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                            <Box
                                component="span"
                                sx={{
                                    ...blueGradientSxDown(theme),
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                }}
                            >
                                Legal Documentation
                            </Box>
                        </Typography>
                    </Box>
                    <Typography variant="h2" fontWeight={500} sx={{ color: 'text.primary', letterSpacing: '-0.02em' }}>{title}</Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: '600px', lineHeight: 1.6 }}>{description}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', opacity: 0.6, mt: 2 }}>Last updated: {lastUpdated}</Typography>
                </Stack>
            </Container>

            <Stack height={{ xs: "60px", lg: "100px" }} width="100%" sx={{
                background: "linear-gradient(-180deg, rgba(11, 11, 11, 0) 0%, #0B0B0B 100%)",
                position: "absolute", bottom: 0, zIndex: 1
            }} />
        </Stack>
    );
}

function LegalSidebar({ data }: { data: LegalSection[] }) {
    return (
        <Box sx={{ 
            position: 'sticky', 
            top: '120px',
            maxHeight: 'calc(100vh - 160px)', 
            overflowY: 'auto',
            pr: 2,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { 
                bgcolor: 'rgba(255,255,255,0.1)', 
                borderRadius: 10 
            }
        }}>
            <Typography variant="subtitle2" sx={{ color: 'text.primary', mb: 3, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Table of Contents
            </Typography>
            <Stack gap={2}>
                {data.map((section) => (
                    <MuiLink 
                        key={`link-${section.id}`} href={`#${section.id}`} underline="none"
                        sx={{ color: 'text.secondary', transition: 'color 0.2s ease', fontSize: '0.875rem', '&:hover': { ...(blueGradientSxDown(theme) as object), WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" } }}
                    >
                        {section.title}
                    </MuiLink>
                ))}
            </Stack>
        </Box>
    );
}

function LegalContent({ data }: { data: LegalSection[] }) {
    return (
        <Box sx={{ 
            maxHeight: { md: 'calc(100vh - 160px)' },
            overflowY: { md: 'auto' },
            pr: { md: 4 },
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 10 }
        }}>
            <Stack gap={{ xs: 8, md: 10 }}>
                {data.map((section) => (
                    <Box key={`section-${section.id}`} id={section.id} sx={{ scrollMarginTop: '20px' }}>
                        <Typography variant="h5" fontWeight={400} sx={{ color: 'text.primary', mb: 3, pb: 2, borderBottom: `1px solid rgba(255, 255, 255, 0.1)` }}>
                            {section.title}
                        </Typography>
                        
                        <Stack gap={3}>
                            {section.content.map((item, idx) => {
                                if (typeof item === 'string') {
                                    return (
                                        <Typography key={`p-${idx}`} variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, fontWeight: 300 }}>
                                            {item}
                                        </Typography>
                                    );
                                }
                                if ('table' in item) {
                                    return (
                                        <TableContainer key={`table-${idx}`} sx={{ mt: 2, mb: 2, borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)', overflowX: 'auto' }}>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor: 'rgba(255, 255, 255, 0.03)' }}>
                                                    <TableRow>
                                                        {['Cookie Name', 'Supplier', 'Scope', 'Term', 'Category', 'More Info'].map((head) => (
                                                            <TableCell key={head} sx={{ color: 'text.primary', fontWeight: 600, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', py: 2 }}>{head}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {item.table.map((row, rIdx) => (
                                                        <TableRow key={rIdx}>
                                                            <TableCell sx={{ color: 'text.secondary', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{row.name}</TableCell>
                                                            <TableCell sx={{ color: 'text.secondary', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{row.supplier}</TableCell>
                                                            <TableCell sx={{ color: 'text.secondary', borderBottom: '1px solid rgba(255, 255, 255, 0.05)', minWidth: 200 }}>{row.scope}</TableCell>
                                                            <TableCell sx={{ color: 'text.secondary', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{row.term}</TableCell>
                                                            <TableCell sx={{ color: 'text.secondary', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>{row.category}</TableCell>
                                                            <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                                                {row.link !== "#" ? (
                                                                    <Link href={row.link} target="_blank" style={{ color: 'theme.palette.text.primary', textDecoration: 'none' }}>{row.linkText}</Link>
                                                                ) : row.linkText}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    );
                                }
                                if ('list' in item) {
                                    return (
                                        <Box component="ul" key={`list-${idx}`}>
                                            {item.list.map((link, lIdx) => (
                                                <Box component="li" key={lIdx} sx={{ color: 'text.secondary' }}>
                                                    <Link href={link.href} target="_blank" style={{ color: 'theme.palette.text.primary', textDecoration: 'none', fontSize: '1rem' }}>
                                                        {link.text}
                                                    </Link>
                                                </Box>
                                            ))}
                                        </Box>
                                    );
                                }

                                return null;
                            })}
                        </Stack>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}

export default function LegalPageLayout({ title, description, lastUpdated, data }: LegalPageLayoutProps) {
    return (
        <Stack component="main" width="100%" alignItems="center" sx={{ bgcolor: '#0B0B0B', position: 'relative' }}>
            
            <LegalHero title={title} description={description} lastUpdated={lastUpdated} />

            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 8 }, position: 'relative', zIndex: 1 }}>
                <Grid container spacing={{ xs: 6, md: 10 }}>
                    <Grid size={{ xs: 12, md: 4 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                        <LegalSidebar data={data} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }}>
                        <LegalContent data={data} />
                    </Grid>
                </Grid>
            </Container>

            <Stack width="100%" alignItems="center" position="relative" sx={{ borderTop: `1px solid ${theme.palette.brand.border1.main}` }}>
                <Footer />
            </Stack>
        </Stack>
    );
}